<template>
  <div class="base-upload" :class="uploadDirection">
    <slot v-if="!imageInTrigger" name="preview" :fileList="fileList">
      <div v-for="(item, index) in fileList" :key="item.url" class="base-upload__preview">
        <div v-if="type === 'image'" class="base-upload__preview-image">
          <el-image
            :style="imageStyle"
            :src="item.url"
            v-bind="imgAttr"
          />
          <div class="base-upload__preview-mask">
            <i class="el-icon-delete" @click="handleDelete(index)" />
          </div>
        </div>

        <div v-if="type === 'file'" class="base-upload__preview-file">
          <base-overflow-tip
            v-if="!allowDownload"
            color="var(--button-text-color)"
          >
            {{ item.filename || item.name || '附件' + (index + 1) }}
          </base-overflow-tip>

          <el-link
            v-else
            @click="handleDownloadFile(item.url, item.filename || item.name || '附件' + (index + 1))"
          >
            <base-overflow-tip color="var(--main-color)">{{ item.filename || item.name || '附件' + (index + 1) }}</base-overflow-tip>
          </el-link>
          <i v-if="isCanDelete" class="el-icon-delete" @click="handleDelete(index)" />
        </div>
      </div>
    </slot>
    <template v-if="isShowUpload">
      <el-upload
        ref="baseUpload"
        class="base-upload__trigger"
        name="file"
        action=""
        :on-exceed="handleOnExceed"
        :before-upload="beforeUpload"
        :http-request="httpRequest"
        :show-file-list="false"
        :multiple="isMultiple"
        v-bind="$attrs"
      >
        <div
          slot="trigger"
          :class="['base-upload__trigger-box']"
          :style="uploadStyle"
        >
          <slot name="trigger">
            <el-image
              v-if="imageInTrigger && type === 'image' && fileList.length > 0"
              :style="uploadStyle"
              :src="value"
              v-bind="imgAttr"
            />
            <template v-else>
              <!-- <i class="el-icon-upload" /> -->
              <img src="@/assets/images/icon_upload.png" style="width:50px;height:50px" alt=""/>
            </template>
          </slot>
        </div>
      </el-upload>
      <span v-if="uploadText" class="upload-text">{{ uploadText }}</span>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { downloadFile } from '@/api/common'
import { getToken } from '@/utils/auth'
import { pxToRem } from '@/utils'
export default {
  name: 'BaseUpload',
  components: {},
  props: {
    value: {
      type: String,
      default: ''
    },
    width: {
      type: String,
      default: '64px'
    },
    height: {
      type: String,
      default: '64px'
    },
    imgAttr: {
      type: Object,
      default: () => ({})
    },
    action: {
      type: String,
      default: '/adminapi/attachment/attachment/uploads'
    },
    files: {
      type: [Array, String], // 编辑回显的数据
      default: ''
    },
    fileNames: { // 文件名称
      type: [String, Array],
      default: ''
    },
    showUpload: {
      type: Boolean,
      default: true
    },
    multiple: Boolean,
    maxLength: {
      type: [Number, String],
      default: 6
    },
    uploadText: {
      type: String,
      default: ''
    },
    limit: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'image'
    },
    isCanDelete: { // 是否可删除
      type: Boolean,
      default: true
    },
    isHand: Boolean, // 如果需要手动上传
    onlyAllowExcel: { // 是否只允许excel附件
      type: Boolean,
      default: true
    },
    allowDownload: { // 附件是否允许下载
      type: Boolean,
      default: false
    },
    notAllowImage: { // 如果上传附件，不允许把图片当附件上传
      type: Boolean,
      default: true
    },
    imageInTrigger: Boolean, // 图片是否直接显示在上传按钮上面
    direction: { // 上传附件的排列方式 vertical | horizontal
      type: String,
      default: 'horizontal'
    },
    disabled:{
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fileList: [],

      singlePickerFileUrl: '', // 单文件上传时选择的数据
      multiplePickerFileUrls: [] // 多文件上传时选择的数据
    }
  },
  computed: {
    uploadUrl() {
      return `${process.env.VUE_APP_BASE_UPLOAD}${this.action}`
    },
    isShowUpload() {
      if (this.limit) {
        return this.fileList.length < this.maxLength
      }
      return this.showUpload
    },
    isMultiple() {
      return this.multiple || this.maxLength > 1
    },
    uploadStyle() {
      return {
        width: pxToRem(this.width),
        height: pxToRem(this.height)
      }
    },
    imageStyle() {
      if (this.imgAttr.width) {
        return {
          width: pxToRem(this.imgAttr.width),
          height: pxToRem(this.imgAttr.height) || pxToRem(this.imgAttr.width)
        }
      }
      return this.uploadStyle
    },
    uploadDirection() {
      return [`base-upload--${this.direction}`]
    }
  },
  watch: {
    files: {
      handler(value) {
        if (typeof value === 'string' && value) {
          this.fileList = []
          const fileNames = this.fileNames || ''
          value.split(',').forEach((item, index) => {
            this.fileList.push({
              url: item,
              filename: fileNames.split(',')[index] || ''
            })
          })
        } else if (Array.isArray(value)) {
          this.fileList = [...value]
        }
        this.multiplePickerFileUrls = this.fileList.map(item => item.url)
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    handleOnExceed(file, fileList) {
      const isExceed = this.fileList.length + fileList.length > this.maxLength
      if (this.limit && this.isMultiple && isExceed) {
        this.$message.error(`最多上传${this.maxLength}个文件`)
        return false
      }
    },
    beforeUpload(file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/x-icon'
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.doc') || file.name.endsWith('.docx') || file.name.endsWith('.pdf')
      if (this.type === 'file') {
        if (this.onlyAllowExcel && !isExcel) {
          this.$message.error('仅支持上传文件格式的附件!')
          return false
        }
        if (!this.onlyAllowExcel && isJpgOrPng && this.notAllowImage) {
          this.$message.error('请上传非图片格式的附件')
          return false
        }
      }

      if (this.type === 'image') {
        if (!isJpgOrPng) {
          this.$message.error('请上传 .jpeg 或 .jpg 或 .png 或 .ico 格式的图片')
          return false
        }
      }

      // 如果需要外面手动点击上传，把file传出去
      if (this.isHand) {
        this.$emit('change', file)
        return false
      }

      return true
    },
    httpRequest(file) {
      const formData = new FormData()
      formData.append('file[]', file.file)
      axios.post(this.uploadUrl, formData, {
        headers: {
          'Authori-zation': 'Bearer ' + getToken()
        }
      }).then((res) => {
        if (res.status === 200) {
          const { status, msg, data } = res.data
          if (status === 200) {
            if (this.isMultiple) {
              this.fileList = [...this.fileList, ...data]
              this.multiplePickerFileUrls.push(data[0].url)
              this.$emit('change', { fileList: this.fileList, file: file.file })
              this.$emit('input', this.multiplePickerFileUrls.join(','))
            } else {
              this.singlePickerFileUrl = data[0].url
              this.fileList = [...data]
              this.$emit('change', { fileList: this.fileList, file: file.file })
              this.$emit('input', this.singlePickerFileUrl)
            }
          } else {
            this.$message.error(msg)
          }
        }
      })
    },
    handleDelete(index) {
      this.fileList.splice(index, 1)
      const fileUrl = []
      this.fileList.forEach(item => {
        fileUrl.push(item.url)
      })
      if (this.isMultiple) {
        this.multiplePickerFileUrls.splice(index, 1)
      }
      this.$emit('change', { fileList: this.fileList })
      this.$emit('input', fileUrl.join(','))
    },
    handleDownloadFile(file, name) {
      downloadFile(file, name)
    }
  }
}
</script>
<style lang="scss" scoped>
.base-upload {
  display: flex;
  &--horizontal {
    align-items: flex-end;
    flex-wrap: wrap;
  }
  &--vertical {
    flex-direction: column;
    align-items: flex-start;
  }
  &__preview {
    &-image {
      line-height: 1;
      margin-right: 10px;
      margin-bottom: 10px;
      position: relative;
      border-radius: 4px;
      &:hover .base-upload__preview-mask {
        display: flex;
      }
    }
    &-file {
      display: flex;
      align-items: center;
      margin-right: 10px;
      margin-bottom: 10px;
      .el-icon-delete {
        margin-left: 10px;
        font-size: 18px;
        cursor: pointer;
      }
    }
    &-mask {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: none;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      .el-icon-delete {
        margin: 0 4px;
        font-size: 20px;
        color: #fff;
        cursor: pointer;
      }
    }
  }
  &__trigger {
    margin-bottom: 10px;
    &-box {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      // border: 1px dashed #ccc;
      border-radius: 4px;
      cursor: pointer;
      .el-icon-upload {
        font-size: 28px;
      }
    }
    .upload-text {
      line-height: 1;
      margin-top: 5px;
      font-size: 12px;
    }
  }
}
</style>
